import { Button } from "@package/subframe/components/Button";
import { Switch } from "@package/subframe/components/Switch";
import { FeatherAlertTriangle } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  fetchPinReadiness,
  type PinReadinessUser,
} from "@/features/pin-management/pin-readiness-api";
import {
  getGetNurseryQueryControllerGetQueryKey,
  useTogglePinLoginCommandControllerToggle,
} from "@/generated/nurseries/nurseries";

interface BranchPinLoginToggleProps {
  nurseryId: string;
  pinLoginEnabled: boolean;
  /** Whether the current user may change the setting (platform/org admins). */
  canManage: boolean;
}

/**
 * PIN login setting for a single nursery, shown on the branch detail page.
 * Enabling lets staff sign in on shared devices with a name + PIN instead of
 * email/password, across every device linked to this nursery. Before enabling,
 * the readiness check lists staff with no PIN — they'd be unable to log in on
 * a shared device until a manager sets a temporary PIN.
 */
export function BranchPinLoginToggle({
  nurseryId,
  pinLoginEnabled,
  canManage,
}: BranchPinLoginToggleProps) {
  const queryClient = useQueryClient();
  const togglePinLogin = useTogglePinLoginCommandControllerToggle();

  const [isCheckingReadiness, setIsCheckingReadiness] = useState(false);
  const [usersWithoutPin, setUsersWithoutPin] = useState<
    PinReadinessUser[] | null
  >(null);

  function applyToggle(next: boolean) {
    togglePinLogin.mutate(
      { id: nurseryId, data: { pinLoginEnabled: next } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetNurseryQueryControllerGetQueryKey(nurseryId),
          });
          toast.success(next ? "PIN login enabled." : "PIN login disabled.");
        },
        onError: () =>
          toast.error("Couldn't update PIN login. Please try again."),
      },
    );
  }

  async function handleToggle(next: boolean) {
    if (!next) {
      applyToggle(false);
      return;
    }

    // Enabling: check every member has a PIN first and warn if not. The check
    // failing must not brick the toggle — fall through to enabling.
    setIsCheckingReadiness(true);
    try {
      const readiness = await fetchPinReadiness(nurseryId);
      if (!readiness.ready) {
        setUsersWithoutPin(readiness.usersWithoutPin);
        return;
      }
    } catch {
      // Fall through: the warning is best-effort, the server stays the gate.
    } finally {
      setIsCheckingReadiness(false);
    }
    applyToggle(true);
  }

  const isPending = togglePinLogin.isPending || isCheckingReadiness;

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-secondary-beige-200 p-6">
      <span className="text-heading-3 font-heading-3 text-default-font">
        PIN login
      </span>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-body font-body text-default-font">
            Let staff sign in on shared devices with a name and PIN
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Applies to every shared device linked to this nursery. Make sure
            everyone has a PIN set before enabling.
          </span>
        </div>
        <Switch
          checked={pinLoginEnabled}
          disabled={!canManage || isPending}
          onCheckedChange={(next) => void handleToggle(next)}
          aria-label="Enable PIN login"
        />
      </div>
      {!canManage && (
        <span className="text-caption font-caption text-subtext-color italic">
          Contact your nursery group admin to change this.
        </span>
      )}

      <Dialog
        open={usersWithoutPin !== null}
        onOpenChange={(open) => {
          if (!open) setUsersWithoutPin(null);
        }}
      >
        <DialogContent showCloseButton={false} className="max-w-md p-0">
          <DialogTitle className="sr-only">
            Some staff have no PIN yet
          </DialogTitle>

          <div className="flex flex-col gap-4 px-6 py-6">
            <div className="flex items-start gap-3">
              <FeatherAlertTriangle className="mt-1 flex-none text-error-700" />
              <div className="flex flex-col gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Some staff have no PIN yet
                </span>
                <span className="text-body font-body text-subtext-color">
                  These staff members won&apos;t be able to log in on a shared
                  device until you set a temporary PIN for them (People → select
                  staff → Set temporary PIN).
                </span>
              </div>
            </div>

            <ul className="max-h-56 overflow-y-auto rounded-md border border-solid border-secondary-beige-200 px-4 py-3">
              {(usersWithoutPin ?? []).map((user) => (
                <li
                  key={user.userId}
                  className="py-1 text-body font-body text-default-font"
                >
                  {user.displayName}
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-end gap-3">
              <Button
                variant="neutral-primary"
                size="large"
                onClick={() => setUsersWithoutPin(null)}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={() => {
                  setUsersWithoutPin(null);
                  applyToggle(true);
                }}
              >
                Enable anyway
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
